import { useCallback } from 'react';
import { apiPut } from '../client';
import { useNotesStore } from '@/store/notesStore';
import type { Note } from '@/store/notesStore';
import { useNotesListContext } from '@/contexts/NotesListContext';
import { triggerSuccessMessage, triggerErrorMessage } from '@/components/ui/toast/notifications';

const FADE_DURATION_MS = 220;

/**
 * Encapsulates the full optimistic pin/unpin flow:
 *
 * 1. Optimistically flip `pinned` in store.
 * 2. PUT /app/v1/note/:id { pinned }.
 * 3. On success:
 *    a. Record index before refetch.
 *    b. Await refetch (refreshes store with server-ordered list + current filters).
 *    c. If note moved to a new index → briefly fade out the card at its new
 *       position, then let it snap into view (no enter animation).
 *    d. Show success toast.
 * 4. On failure:
 *    a. Revert optimistic update in store.
 *    b. Show error toast.
 */
export function usePinNote() {
  const updateNote = useNotesStore((s) => s.updateNote);
  const { refetch, setFadingNoteId } = useNotesListContext();

  const togglePin = useCallback(
    async (note: Note) => {
      if (!note.id) return;

      const noteId = note.id;
      const previousPinned = note.pinned ?? false;
      const newPinned = !previousPinned;

      // 1. Optimistic update
      updateNote(noteId, { pinned: newPinned });

      try {
        // 2. Persist to server
        await apiPut(`/app/v1/note/${noteId}`, { pinned: newPinned });

        // 3a. Record the note's current position BEFORE the list is reloaded
        const oldIndex = useNotesStore.getState().notes.findIndex((n) => n.id === noteId);

        // 3b. Reload the list with current filters/sorting
        await refetch();

        // 3c. Detect whether the note moved in the refreshed list
        const newIndex = useNotesStore.getState().notes.findIndex((n) => n.id === noteId);

        if (oldIndex !== newIndex) {
          // Note changed position — apply a brief fade so the user notices the reorder.
          // At this point the list has already re-rendered with the note at its new
          // position; the fade-out animation plays there, then the note snaps visible.
          setFadingNoteId(noteId);
          await new Promise<void>((resolve) => setTimeout(resolve, FADE_DURATION_MS));
          setFadingNoteId(null);
        }

        // 3d. Success toast
        triggerSuccessMessage(newPinned ? 'Note pinned' : 'Note unpinned');
      } catch {
        // 4. Revert optimistic update and show error
        updateNote(noteId, { pinned: previousPinned });
        triggerErrorMessage('Failed to ' + (newPinned ? 'pin' : 'unpin') + ' note');
      }
    },
    [updateNote, refetch, setFadingNoteId],
  );

  return { togglePin };
}

